import { useState, useEffect, useCallback } from 'react';
import { Rocket, Eye, Copy, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@easyflow/ui-frontend/components/ui/dialog';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { apiClient } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { FlowDiagram } from '@/components/dialplan-flow/FlowDiagram';
import { getErrorMessage } from '@/lib/utils';


interface DialplanContext {
  id: string;
  tenant_id: string;
  name: string;
  description: string;
  context_type: string;
  enabled: boolean;
  priority: number;
  created_at: string;
  updated_at: string;
}

export default function DialplanContextManager() {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [context, setContext] = useState<DialplanContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [deploying, setDeploying] = useState(false);
  const [showXMLModal, setShowXMLModal] = useState(false);
  const [contextXML, setContextXML] = useState('');
  const [loadingXML, setLoadingXML] = useState(false);
  
  const [formData, setFormData] = useState({
    description: '',
    enabled: true,
    priority: 100
  });

  const loadContext = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get<{ success?: boolean; data?: { context?: DialplanContext } }>('/dialplan/context');
      
      const rd = response.data;
      if (rd?.success && rd?.data?.context) {
        const ctx = rd.data.context;
        setContext(ctx);
        setFormData({
          description: ctx.description || '',
          enabled: ctx.enabled,
          priority: ctx.priority
        });
      }
    } catch (error) {
      console.error('Error loading context:', error);
      toast({
        title: "Error",
        description: getErrorMessage(error) || "Failed to load context",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  const handleSaveMetadata = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await apiClient.put<{ success?: boolean }>('/dialplan/context/metadata', formData);
      
      if ((response.data)?.success) {
        toast({
          title: "Success",
          description: "Context metadata updated successfully"
        });
        await loadContext();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error) || "Failed to update context",
        variant: "destructive"
      });
    }
  };

  const handleViewXML = async () => {
    try {
      setLoadingXML(true);
      setShowXMLModal(true);
      
      const response = await apiClient.get<{ success?: boolean; data?: { xml?: string } }>('/dialplan/context/xml');
      
      if ((response.data)?.success) {
        setContextXML((response.data).data?.xml || '');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error) || "Failed to load XML",
        variant: "destructive"
      });
    } finally {
      setLoadingXML(false);
    }
  };

  const handleDeploy = async () => {
    try {
      setDeploying(true);
      
      const response = await apiClient.post<{ success?: boolean }>('/dialplan/context/deploy');
      
      if ((response.data)?.success) {
        toast({
          title: "Success",
          description: "Context deployed to FreeSWITCH successfully",
          duration: 5000
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: getErrorMessage(error) || "Failed to deploy context",
        variant: "destructive"
      });
    } finally {
      setDeploying(false);
    }
  };

  const handleCopyXML = () => {
    navigator.clipboard.writeText(contextXML);
    toast({
      title: "Copied",
      description: "XML copied to clipboard"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!context) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-gray-600">No context found for this tenant</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Context Manager</h1>
        <p className="text-muted-foreground">
          Manage your tenant dialplan context. Modify routes via Call Routing and Extensions pages.
        </p>
      </div>

      {/* Context Metadata Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Context: {context.name}</span>
            <Badge variant={context.enabled ? "default" : "secondary"}>
              {context.enabled ? "Enabled" : "Disabled"}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveMetadata}>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Context Name (Read-Only)</Label>
                <Input value={context.name} disabled className="bg-gray-50" />
                <p className="text-xs text-muted-foreground mt-1">
                  Context name matches your tenant domain (convention-based routing)
                </p>
              </div>
              
              <div className="col-span-2">
                <Label>Description</Label>
                <Input 
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Enter context description"
                />
              </div>
              
              <div>
                <Label>Priority</Label>
                <Input 
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value) || 100})}
                  min="1"
                  max="999"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Lower number = higher priority
                </p>
              </div>
              
              <div className="flex items-center gap-3 pt-6">
                <Switch 
                  checked={formData.enabled}
                  onCheckedChange={(checked) => setFormData({...formData, enabled: checked})}
                />
                <Label>Enabled</Label>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 mt-6">
              <Button type="submit" variant="default">
                Save Metadata
              </Button>
              <Button type="button" variant="outline" onClick={handleViewXML}>
                <Eye className="h-4 w-4 mr-2" />
                View Generated XML
              </Button>
              <Button 
                type="button" 
                variant="default" 
                className="bg-green-600 hover:bg-green-700"
                onClick={handleDeploy}
                disabled={deploying}
              >
                {deploying ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deploying...
                  </>
                ) : (
                  <>
                    <Rocket className="h-4 w-4 mr-2" />
                    Deploy to FreeSWITCH
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* React Flow Visualization */}
      <Card>
        <CardHeader>
          <CardTitle>Context Flow Diagram (Read-Only)</CardTitle>
          <p className="text-sm text-muted-foreground">
            Visual representation of your dialplan routing. 
            Modify routes via <strong>Call Routing</strong>, <strong>Extensions</strong>, and <strong>Time Conditions</strong> pages.
          </p>
        </CardHeader>
        <CardContent>
          <div className="h-[600px] border rounded bg-gray-50">
            <FlowDiagram tenantId={user?.tenant_id ?? undefined} />
          </div>
        </CardContent>
      </Card>

      {/* XML Viewer Modal */}
      <Dialog open={showXMLModal} onOpenChange={setShowXMLModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Generated Context XML (Read-Only)</DialogTitle>
            <DialogDescription>
              This XML is auto-generated from your routes and extensions.
              You can copy it but not edit it directly.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden relative">
            {loadingXML ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : (
              <>
                <pre className="bg-gray-900 text-green-400 p-4 rounded overflow-auto h-full text-xs font-mono">
                  <code>{contextXML}</code>
                </pre>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="absolute top-2 right-2 bg-white"
                  onClick={handleCopyXML}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

